import { SEOLayout } from './SEOLayout';

const Terms = () => (
  <SEOLayout
    title="Terms of Service | Vergo"
    description="The terms governing use of Vergo, the AI tool discovery and comparison platform." keywords="Vergo terms of service, terms and conditions, legal"
  >
    <article className="prose prose-invert max-w-3xl mx-auto px-4 py-16">
      <h1>Terms of Service</h1>
      <p><em>Last updated: May 2026</em></p>

      <p>
        Welcome to Vergo. By accessing or using vergoai.app (the "Service"), you agree to these
        Terms of Service ("Terms"). If you do not agree, do not use the Service.
      </p>

      <h2>1. Who we are</h2>
      <p>
        The Service is operated by the Vergo team ("Vergo", "we", "us"). Contact details are
        available on the <a href="/contact">contact page</a>.
      </p>

      <h2>2. Eligibility</h2>
      <p>
        You must be at least 13 years old to use the Service, and at least 16 years old in
        jurisdictions where that is the minimum age for digital consent. By creating an account,
        you confirm that you meet these requirements.
      </p>

      <h2>3. Accounts</h2>
      <p>
        Some features require an account. You agree to provide accurate information, keep your
        credentials secure, and accept responsibility for all activity under your account. We may
        suspend or terminate accounts that violate these Terms.
      </p>

      <h2>4. Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Scrape, crawl, or copy the Service or its content except as permitted by our robots.txt.</li>
        <li>Attempt to interfere with or compromise the security of the Service.</li>
        <li>Use the Service to distribute malware, spam, or unlawful content.</li>
        <li>Misrepresent your identity or impersonate any person or entity.</li>
        <li>Use automated systems to submit reviews, comparisons, or tool submissions.</li>
      </ul>

      <h2>5. User content</h2>
      <p>
        You retain ownership of content you submit (reviews, ratings, tool submissions, comments).
        By submitting content, you grant Vergo a worldwide, non-exclusive, royalty-free license to
        host, display, reproduce, and distribute that content in connection with operating and
        promoting the Service. You represent that you have the rights to submit the content and
        that it does not violate any third-party rights or laws.
      </p>

      <h2>6. Editorial content and third-party tools</h2>
      <p>
        Reviews, ratings, comparisons, and recommendations on Vergo are editorial opinion based on
        our hands-on testing and research. They are provided for informational purposes only and
        do not constitute professional, legal, financial, or technical advice. AI tools listed on
        Vergo are operated by third parties under their own terms of service and privacy policies.
        You are responsible for reviewing those terms before using any third-party tool.
      </p>

      <h2>7. Affiliate disclosure</h2>
      <p>
        Some links on Vergo are affiliate links, meaning we may earn a commission if you sign up
        for a tool through them. Affiliate relationships never influence whether a tool is
        included, how it is rated, or how it is ranked. See our <a href="/how-we-review">review
        methodology</a> for full details.
      </p>

      <h2>8. Subscriptions and payments</h2>
      <p>
        Optional paid plans are billed in advance on a recurring basis. You can cancel at any time
        from your account settings; cancellation takes effect at the end of the current billing
        period. We do not refund partial periods except where required by law.
      </p>

      <h2>9. Intellectual property</h2>
      <p>
        The Vergo name, logo, software, design, and editorial content are owned by Vergo or its
        licensors and are protected by intellectual property laws. You may not use them without
        our written permission, except for fair-use linking and quoting with attribution.
      </p>

      <h2>10. Disclaimers</h2>
      <p>
        The Service is provided "as is" and "as available" without warranties of any kind, express
        or implied. We do not warrant that the Service will be uninterrupted, error-free, or that
        information will always be accurate or current.
      </p>

      <h2>11. Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, Vergo will not be liable for any indirect,
        incidental, consequential, special, or punitive damages, or loss of profits, revenue,
        data, or goodwill, arising from your use of the Service. Our total liability for any
        claim relating to the Service will not exceed the greater of (a) the amount you paid us
        in the 12 months before the claim, or (b) USD 50.
      </p>

      <h2>12. Indemnification</h2>
      <p>
        You agree to indemnify and hold harmless Vergo from any claim arising out of your use of
        the Service, your content, or your violation of these Terms.
      </p>

      <h2>13. Changes to the Terms</h2>
      <p>
        We may update these Terms from time to time. Material changes will be announced on the
        site or by email. Continued use after changes take effect constitutes acceptance.
      </p>

      <h2>14. Governing law</h2>
      <p>
        These Terms are governed by the laws of the jurisdiction in which Vergo is operated,
        without regard to conflict-of-laws principles. Any dispute will be resolved in the courts
        of that jurisdiction, except where applicable consumer-protection law gives you the right
        to bring proceedings in your country of residence.
      </p>

      <h2>15. Contact</h2>
      <p>
        Questions about these Terms? Email <a href="mailto:legal@vergoai.app">legal@vergoai.app</a>.
      </p>
    </article>
  </SEOLayout>
);

export default Terms;
